import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Check, Dumbbell, Zap, School } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

export function Pricing() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const pricingRef = useRef<HTMLDivElement>(null);
  const [annual, setAnnual] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animated-element');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('appear');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (pricingRef.current) {
      observer.observe(pricingRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  const discount = 20; // 20% discount for annual billing

  // Helper function to safely handle feature arrays
  const getFeaturesList = (pricingKey: string): string[] => {
    const featuresPath = `pricing.${pricingKey}.features`;
    // Try to get individual feature items
    const features = [];
    
    // Look for indexed items (features[0], features[1], etc.)
    for (let i = 0; i < 5; i++) {
      const featureKey = `${featuresPath}[${i}]`;
      const feature = t(featureKey);
      
      // If the translation key is returned unchanged, we've reached the end of the array
      if (feature === featureKey) {
        break;
      }
      
      features.push(feature);
    }
    
    return features.length > 0 ? features : [];
  };

  // Get features for each pricing tier
  const starterFeatures = getFeaturesList('starter');
  const professionalFeatures = getFeaturesList('professional');
  const eliteFeatures = getFeaturesList('elite');
  
  // Handler for when a user selects a pricing plan
  const handleSelectPlan = (plan: string) => {
    if (isAuthenticated) {
      navigate('/payment', { 
        state: { 
          plan,
          annual
        } 
      });
    } else {
      toast({
        title: t('pricing.login_required'),
        description: t('pricing.login_to_continue'),
        variant: "default",
      });
      navigate('/register', {
        state: {
          redirect: '/payment',
          plan,
          annual
        }
      });
    }
  };

  return (
    <section id="pricing" className="py-24 relative" ref={pricingRef}>
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-purple-50 to-white -z-10" />
      <div className="absolute top-1/3 right-1/5 w-64 h-64 rounded-full bg-blue-300/10 filter blur-3xl -z-10" />
      <div className="absolute bottom-1/3 left-1/5 w-64 h-64 rounded-full bg-purple-300/10 filter blur-3xl -z-10" />
      
      <div className="container px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="animated-element">
            <span className="inline-block bg-gradient-to-r from-purple-500 to-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium mb-6">
              {t('nav.pricing')}
            </span>
          </div>
          
          <h2 className="animated-element text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
            {t('pricing.title')}
          </h2>
          
          <p className="animated-element text-xl text-muted-foreground mb-12">
            {t('pricing.subtitle')}
          </p>
          
          <div className="animated-element flex items-center justify-center gap-4 mb-12">
            <span className={cn("text-sm font-medium", !annual ? "text-foreground" : "text-muted-foreground")}>
              {t('pricing.monthly')}
            </span>
            
            <Switch
              checked={annual}
              onCheckedChange={setAnnual}
              className="data-[state=checked]:bg-purple-600"
            />
            
            <span className="relative">
              <span className={cn("text-sm font-medium", annual ? "text-foreground" : "text-muted-foreground")}>
                {t('pricing.yearly')}
              </span>
              {annual && (
                <Badge variant="outline" className="absolute -right-12 -top-5 bg-green-100 text-green-800 border-green-200 text-xs py-0 px-1.5">
                  {discount}% off
                </Badge>
              )}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols m-3 gap-8 max-w-7xl mx-auto">
          {/* Starter Plan */}
          <div className="animated-element group relative bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-blue-500"></div>
            <div className="p-8">
              <h3 className="flex items-center text-xl font-bold mb-2">
                <Dumbbell className="mr-2 h-5 w-5 text-blue-500" />
                {t('pricing.starter.title')}
              </h3>
              <div className="flex items-baseline mt-4 mb-6">
                <span className="text-4xl font-extrabold">${annual ? 
                  Math.round(parseFloat(t('pricing.starter.price')) * (100 - discount) / 100) : 
                  t('pricing.starter.price')}</span>
                <span className="text-slate-500 ml-2">/ {annual ? t('pricing.yearly') : t('pricing.monthly')}</span>
              </div>
              <p className="text-muted-foreground mb-6">
                {t('pricing.starter.description')}
              </p>
              <Button className="w-full mb-8 group-hover:bg-blue-600" onClick={() => handleSelectPlan('starter')}>
                {t('pricing.cta')}
              </Button>
              <ul className="space-y-3">
                {starterFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <Check className="h-4 w-4 text-blue-500 mr-3 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Professional Plan */}
          <div className="animated-element group relative bg-white rounded-2xl shadow-xl border border-purple-100 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 md:scale-105 z-10">
            <div className="absolute inset-0 bg-gradient-to-b from-purple-50 to-transparent -z-10"></div>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-blue-500"></div>
            <Badge className="absolute top-5 right-5 bg-gradient-to-r from-purple-500 to-blue-500 border-0">{t('pricing.popular')}</Badge>
            <div className="p-8">
              <h3 className="flex items-center text-xl font-bold mb-2">
                <School className="mr-2 h-5 w-5 text-purple-500" />
                {t('pricing.professional.title')}
              </h3>
              <div className="flex items-baseline mt-4 mb-6">
                <span className="text-5xl font-extrabold">${annual ? 
                  Math.round(parseFloat(t('pricing.professional.price')) * (100 - discount) / 100) : 
                  t('pricing.professional.price')}</span>
                <span className="text-slate-500 ml-2">/ {annual ? t('pricing.yearly') : t('pricing.monthly')}</span>
              </div>
              <p className="text-muted-foreground mb-6">
                {t('pricing.professional.description')}
              </p>
              <Button className="w-full mb-8 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white" onClick={() => handleSelectPlan('professional')}>
                {t('pricing.cta')}
              </Button>
              <ul className="space-y-3">
                {professionalFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <Check className="h-4 w-4 text-purple-500 mr-3 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Elite Plan 
          <div className="animated-element group relative bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-purple-600"></div>
            <div className="p-8">
              <h3 className="flex items-center text-xl font-bold mb-2">
                <svg className="mr-2 h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1v3M12 20v3M4.22 4.22l2.12 2.12m11.32 11.32l2.12 2.12M1 12h3m16 0h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="5" fill="currentColor"/>
                </svg>
                {t('pricing.elite.title')}
              </h3>
              <div className="flex items-baseline mt-4 mb-6">
                <span className="text-4xl font-extrabold">${annual ? 
                  Math.round(parseFloat(t('pricing.elite.price')) * (100 - discount) / 100) : 
                  t('pricing.elite.price')}</span>
                <span className="text-slate-500 ml-2">/ {annual ? t('pricing.yearly') : t('pricing.monthly')}</span>
              </div>
              <p className="text-muted-foreground mb-6">
                {t('pricing.elite.description')}
              </p>
              <Button variant="outline" className="w-full mb-8 border-blue-600 text-blue-600 group-hover:bg-blue-600 group-hover:text-white" onClick={() => handleSelectPlan('elite')}>
                {t('pricing.cta')}
              </Button>
              <ul className="space-y-3">
                {eliteFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <Check className="h-4 w-4 text-blue-600 mr-3 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          */}
        </div>
      </div>
    </section>
  );
}
